/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：研究工作区主题工具，定义策略信号、工作区壳层和特定路径的视觉/布局辅助判断。
 * - English: Research-workspace theme utilities defining strategy-signal themes, shell styling helpers, and path-specific layout checks.
 *
 * 调用关系 / Call Flow
 * - 中文：由 app.ts/app.jsx、策略中心和工作区组件读取。
 * - English: Read by app.ts/app.jsx, the strategy center, and workspace components.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧或 Umi 构建侧，不直接访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Runs only in the browser or Umi build layer; it does not access databases, write SQL, or copy server-side fractal-core algorithms.
 *
 * 上线注意 / Release Notes
 * - 中文：作为智能研究工作台前端上线清单的一部分，修改时必须保持路由、白名单登录、报告/策略入口和生产构建校验一致。
 * - English: Part of the intelligence-workbench frontend rollout scope; keep routes, whitelist login, report/strategy entry points, and production-build checks aligned.
 */

import { getReportVisualTheme } from '@/utils/reportVisualThemes';

export const STRATEGY_SIGNAL_HEADER_THEMES = {
  breakoutWatch: {
    accent: '#2563eb',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 58%, #2563eb 100%)',
    badgeBackground: 'rgba(219, 234, 254, 0.96)',
    badgeColor: '#1d4ed8',
    metaBackground: 'rgba(255, 255, 255, 0.12)',
    metaBorder: 'rgba(255, 255, 255, 0.22)',
    primaryButtonBackground: '#2563eb',
    primaryButtonBorder: '#60a5fa',
    secondaryButtonColor: '#1e3a8a',
    shadow: '0 18px 42px rgba(37, 99, 235, 0.18)',
    summaryBackground: 'linear-gradient(90deg, rgba(37, 99, 235, 0.10) 0%, rgba(37, 99, 235, 0.03) 100%)',
    summaryBorder: '1px solid rgba(96, 165, 250, 0.20)',
    summaryLabelColor: '#1e3a8a',
    summaryValueColor: '#0f172a',
    tableSelectedBackground: 'rgba(37, 99, 235, 0.12)',
    tableSelectedHoverBackground: 'rgba(37, 99, 235, 0.17)',
  },
  uptrendDoubleResistance: {
    accent: '#d97706',
    background: 'linear-gradient(135deg, #111827 0%, #4338ca 46%, #b45309 100%)',
    badgeBackground: 'rgba(254, 243, 199, 0.96)',
    badgeColor: '#92400e',
    metaBackground: 'rgba(255, 255, 255, 0.13)',
    metaBorder: 'rgba(251, 191, 36, 0.30)',
    primaryButtonBackground: '#d97706',
    primaryButtonBorder: '#f59e0b',
    secondaryButtonColor: '#92400e',
    shadow: '0 18px 42px rgba(180, 83, 9, 0.20)',
    summaryBackground: 'linear-gradient(90deg, rgba(217, 119, 6, 0.12) 0%, rgba(67, 56, 202, 0.04) 100%)',
    summaryBorder: '1px solid rgba(251, 191, 36, 0.24)',
    summaryLabelColor: '#92400e',
    summaryValueColor: '#111827',
    tableSelectedBackground: 'rgba(217, 119, 6, 0.13)',
    tableSelectedHoverBackground: 'rgba(217, 119, 6, 0.18)',
  },
  validWavePivot: {
    accent: '#0d9488',
    background: 'linear-gradient(135deg, #052e2b 0%, #0f766e 52%, #0891b2 100%)',
    badgeBackground: 'rgba(204, 251, 241, 0.96)',
    badgeColor: '#115e59',
    metaBackground: 'rgba(255, 255, 255, 0.13)',
    metaBorder: 'rgba(94, 234, 212, 0.30)',
    primaryButtonBackground: '#0d9488',
    primaryButtonBorder: '#2dd4bf',
    secondaryButtonColor: '#115e59',
    shadow: '0 18px 42px rgba(13, 148, 136, 0.20)',
    summaryBackground: 'linear-gradient(90deg, rgba(13, 148, 136, 0.12) 0%, rgba(8, 145, 178, 0.04) 100%)',
    summaryBorder: '1px solid rgba(45, 212, 191, 0.24)',
    summaryLabelColor: '#115e59',
    summaryValueColor: '#052e2b',
    tableSelectedBackground: 'rgba(13, 148, 136, 0.13)',
    tableSelectedHoverBackground: 'rgba(13, 148, 136, 0.18)',
  },
};

const DEFAULT_RESEARCH_WORKSPACE_THEME = {
  panelBackground:
    'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 58%, rgba(30, 64, 175, 0.82) 100%)',
  railBackground:
    'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 58%, rgba(30, 64, 175, 0.74) 100%)',
  shadow: '0 20px 48px rgba(15, 23, 42, 0.16)',
  eyebrowColor: '#93c5fd',
  hoverBorder: 'rgba(96, 165, 250, 0.30)',
  hoverBackground: 'rgba(59, 130, 246, 0.12)',
  activeBorder: 'rgba(245, 158, 11, 0.30)',
  activeBackground: 'rgba(245, 158, 11, 0.16)',
  activeShadow: 'inset 0 0 0 1px rgba(245, 158, 11, 0.10)',
  itemCardBackground: 'rgba(255, 255, 255, 0.05)',
  itemCardBorder: 'rgba(148, 163, 184, 0.12)',
  actionBorder: 'rgba(125, 211, 252, 0.28)',
  actionBackground: 'rgba(59, 130, 246, 0.14)',
  actionHoverBackground: 'rgba(59, 130, 246, 0.20)',
  pillBackground: 'rgba(148, 163, 184, 0.10)',
  pillBorder: 'rgba(148, 163, 184, 0.14)',
};

const STRATEGY_WORKSPACE_THEMES = {
  breakoutWatch: {
    ...DEFAULT_RESEARCH_WORKSPACE_THEME,
    panelBackground: STRATEGY_SIGNAL_HEADER_THEMES.breakoutWatch.background,
    railBackground: 'linear-gradient(180deg, #0f172a 0%, #1e3a8a 58%, #2563eb 100%)',
    shadow: '0 20px 48px rgba(37, 99, 235, 0.18)',
    eyebrowColor: '#bfdbfe',
    hoverBorder: 'rgba(96, 165, 250, 0.34)',
    hoverBackground: 'rgba(37, 99, 235, 0.14)',
    activeBorder: 'rgba(96, 165, 250, 0.44)',
    activeBackground: 'rgba(37, 99, 235, 0.18)',
    activeShadow: 'inset 0 0 0 1px rgba(96, 165, 250, 0.12)',
    itemCardBackground: 'rgba(37, 99, 235, 0.08)',
    itemCardBorder: 'rgba(96, 165, 250, 0.16)',
    actionBorder: 'rgba(96, 165, 250, 0.34)',
    actionBackground: 'rgba(37, 99, 235, 0.15)',
    actionHoverBackground: 'rgba(37, 99, 235, 0.24)',
    pillBackground: 'rgba(96, 165, 250, 0.13)',
    pillBorder: 'rgba(147, 197, 253, 0.22)',
  },
  uptrendDoubleResistance: {
    ...DEFAULT_RESEARCH_WORKSPACE_THEME,
    panelBackground: STRATEGY_SIGNAL_HEADER_THEMES.uptrendDoubleResistance.background,
    railBackground: 'linear-gradient(180deg, #111827 0%, #4338ca 52%, #b45309 100%)',
    shadow: '0 20px 48px rgba(180, 83, 9, 0.20)',
    eyebrowColor: '#fde68a',
    hoverBorder: 'rgba(251, 191, 36, 0.34)',
    hoverBackground: 'rgba(217, 119, 6, 0.14)',
    activeBorder: 'rgba(251, 191, 36, 0.48)',
    activeBackground: 'rgba(217, 119, 6, 0.22)',
    activeShadow: 'inset 0 0 0 1px rgba(251, 191, 36, 0.14)',
    itemCardBackground: 'rgba(217, 119, 6, 0.10)',
    itemCardBorder: 'rgba(251, 191, 36, 0.18)',
    actionBorder: 'rgba(251, 191, 36, 0.38)',
    actionBackground: 'rgba(217, 119, 6, 0.18)',
    actionHoverBackground: 'rgba(217, 119, 6, 0.28)',
    pillBackground: 'rgba(251, 191, 36, 0.13)',
    pillBorder: 'rgba(253, 230, 138, 0.22)',
  },
  validWavePivot: {
    ...DEFAULT_RESEARCH_WORKSPACE_THEME,
    panelBackground: STRATEGY_SIGNAL_HEADER_THEMES.validWavePivot.background,
    railBackground: 'linear-gradient(180deg, #052e2b 0%, #0f766e 56%, #0891b2 100%)',
    shadow: '0 20px 48px rgba(13, 148, 136, 0.20)',
    eyebrowColor: '#99f6e4',
    hoverBorder: 'rgba(45, 212, 191, 0.34)',
    hoverBackground: 'rgba(13, 148, 136, 0.14)',
    activeBorder: 'rgba(45, 212, 191, 0.48)',
    activeBackground: 'rgba(13, 148, 136, 0.22)',
    activeShadow: 'inset 0 0 0 1px rgba(45, 212, 191, 0.14)',
    itemCardBackground: 'rgba(13, 148, 136, 0.10)',
    itemCardBorder: 'rgba(45, 212, 191, 0.18)',
    actionBorder: 'rgba(45, 212, 191, 0.38)',
    actionBackground: 'rgba(13, 148, 136, 0.18)',
    actionHoverBackground: 'rgba(13, 148, 136, 0.28)',
    pillBackground: 'rgba(45, 212, 191, 0.13)',
    pillBorder: 'rgba(153, 246, 228, 0.22)',
  },
};

const STRATEGY_THEME_ROUTES = [
  { prefix: '/strategy/signals/breakoutwatch', themeKey: 'breakoutWatch' },
  { prefix: '/strategy/signals/uptrend-double-resistance', themeKey: 'uptrendDoubleResistance' },
  { prefix: '/strategy/signals/valid-wave-pivot', themeKey: 'validWavePivot' },
];

const buildReportWorkspaceTheme = key => {
  const theme = getReportVisualTheme(key);
  return {
    ...DEFAULT_RESEARCH_WORKSPACE_THEME,
    railBackground: theme.railBackground,
    shadow: theme.railShadow,
    eyebrowColor: theme.railEyebrow,
    hoverBorder: theme.hoverBorder,
    hoverBackground: theme.activeBackground,
    activeBorder: theme.activeBorder,
    activeBackground: theme.activeBackground,
    activeShadow: theme.activeShadow,
    itemCardBackground: theme.railItemBackground,
    itemCardBorder: theme.railItemBorder,
    actionBorder: theme.railActionBorder,
    actionBackground: theme.railActionBackground,
    actionHoverBackground: theme.railActionHoverBackground,
    pillBackground: theme.railPillBackground,
    pillBorder: theme.railPillBorder,
  };
};

const REPORT_WORKSPACE_THEMES = {
  hotspot: buildReportWorkspaceTheme('hotspot'),
  attribution: buildReportWorkspaceTheme('attribution'),
};

const REPORT_THEME_ROUTES = [
  { prefix: '/reports/hotspot', themeKey: 'hotspot' },
  { prefix: '/reports/attribution', themeKey: 'attribution' },
];

const normalizePathname = pathname => String(pathname || '').replace(/\/+$/, '') || '/';

export const resolveResearchWorkspaceTheme = (pathname) => {
  const normalized = normalizePathname(pathname);
  const match = STRATEGY_THEME_ROUTES.find(route => (
    normalized === route.prefix || normalized.startsWith(`${route.prefix}/`)
  ));

  if (match) return STRATEGY_WORKSPACE_THEMES[match.themeKey];

  const reportMatch = REPORT_THEME_ROUTES.find(route => (
    normalized === route.prefix || normalized.startsWith(`${route.prefix}/`)
  ));

  return reportMatch ? REPORT_WORKSPACE_THEMES[reportMatch.themeKey] : DEFAULT_RESEARCH_WORKSPACE_THEME;
};

export const isStrategySignalWorkspacePath = (pathname) => {
  const normalized = normalizePathname(pathname);
  return STRATEGY_THEME_ROUTES.some(route => (
    normalized === route.prefix || normalized.startsWith(`${route.prefix}/`)
  ));
};

export const buildResearchWorkspaceThemeStyle = theme => ({
  '--research-theme-panel-bg': theme.panelBackground,
  '--research-theme-rail-bg': theme.railBackground,
  '--research-theme-shadow': theme.shadow,
  '--research-theme-eyebrow': theme.eyebrowColor,
  '--research-theme-hover-border': theme.hoverBorder,
  '--research-theme-hover-bg': theme.hoverBackground,
  '--research-theme-active-border': theme.activeBorder,
  '--research-theme-active-bg': theme.activeBackground,
  '--research-theme-active-shadow': theme.activeShadow,
  '--research-theme-item-bg': theme.itemCardBackground,
  '--research-theme-item-border': theme.itemCardBorder,
  '--research-theme-action-border': theme.actionBorder,
  '--research-theme-action-bg': theme.actionBackground,
  '--research-theme-action-hover-bg': theme.actionHoverBackground,
  '--research-theme-pill-bg': theme.pillBackground,
  '--research-theme-pill-border': theme.pillBorder,
});
